import type { ElementType } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type { PublicServiceStatus } from "@/v2-status-client/status/status.types"
import StatusSummaryTitle from "@/v2-status-client/status/summary/StatusSummaryTitle"
import StatusSummaryUptime from "@/v2-status-client/status/summary/StatusSummaryUptime"

export type StatusSummaryRowProps = {
  name: string
  description?: string
  status: PublicServiceStatus
  summary: string
  // 30-day pooled uptime %. Hidden when null (no data yet) -- see
  // StatusSummaryUptime for the reasoning.
  uptimePercent30d?: number | null
  // Polymorphic element. Defaults to `<li>` for the providers
  // section that maps the row directly inside a `<ul>`. The
  // category section sets this to `<div>` so the row can sit
  // inside an outer `<li>` that owns the expand/collapse state.
  as?: ElementType
  className?: string
  // Optional anchor id forwarded to the rendered element so
  // deep-link targets (`#category-foo`, `#service-foo`) can land
  // directly on the row. Kept as an explicit prop rather than a
  // catch-all `...rest` so the polymorphic `as` doesn't accidentally
  // swallow handlers that don't make sense at this layer.
  id?: string
}

// Generic "summary row" used by both the Categories and Providers
// sections. Same shape for both because the customer-facing
// vocabulary is identical: dot + name, optional description,
// summary, optional uptime readout. Layout is a 12-col grid so
// titles and uptime numbers line up across rows of different
// sections.
//
// The 30-day history strip used to live in the middle of this row;
// it now renders next to each individual service inside an
// expanded category, so the aggregate row stays informational
// rather than competing with 30 cells of micro-data per platform.
function StatusSummaryRow(props: Readonly<StatusSummaryRowProps>) {
  const {
    name,
    description,
    status,
    summary,
    uptimePercent30d,
    as: As = "li",
    className,
    id,
  } = props
  return (
    <As
      id={id}
      className={cn(
        "grid grid-cols-12 items-center gap-x-4 gap-y-2 border-b border-border/60 py-3 last:border-b-0 sm:gap-x-6",
        className,
      )}
    >
      <div className="col-span-12 sm:col-span-10">
        <StatusSummaryTitle
          name={name}
          description={description}
          status={status}
          summary={summary}
        />
      </div>
      <div className="col-span-12 sm:col-span-2">
        {uptimePercent30d != null && (
          <StatusSummaryUptime uptimePercent30d={uptimePercent30d} />
        )}
      </div>
    </As>
  )
}

export default StatusSummaryRow
